import {
  useState, useRef, useEffect,
} from 'react';
import Head from 'next/head';
import { EmbedFallback } from 'components/PostEmbed/EmbedFallback';
import { Post } from 'lib/types';

const TIKTOK_OEMBED_BASE_URL = 'https://www.tiktok.com/oembed';

interface Props {
  post: Post;
}

export default function TikTok({ post }: Props) {
  const [error, setError] = useState<string | undefined>(undefined);
  const [html, setHTML] = useState<string | undefined>(undefined);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_loaded, setLoaded] = useState(false);
  const ref = useRef(null);

  /**
   * Trigger loaded state when the iframe has loaded
   */

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setError('Error loading TikTok embed');
    }, 10000); // 10 seconds timeout
    /**
     * MutationObserver:
     * https://stackoverflow.com/questions/3219758/detect-changes-in-the-dom
     */
    if (typeof window !== 'undefined' && !('MutationObserver' in window)) {
      return setLoaded(true);
    }
    /**
     * TODO: Check bugs for MutationObserver
     * https://caniuse.com/#feat=mutationobserver
     */

    const elem = ref.current;
    const observer = new MutationObserver((mutationList) => {
      // Get the iframe from the mutation that has added it
      const iframeAdded = mutationList.reduce((acc: Node | undefined, curr) => {
        const iframe = Array.from(curr.addedNodes)
          .find((node) => node.nodeName === 'IFRAME');
        if (iframe) {
          // eslint-disable-next-line no-param-reassign
          acc = iframe;
        }
        return acc;
      }, undefined);
      if (iframeAdded) {
        iframeAdded.addEventListener('load', () => setLoaded(true));
        clearTimeout(timeoutId);
      }
    });
    if (elem) {
      observer.observe(elem, {
        childList: true,
        attributes: true,
        subtree: true,
      });
    }
    return () => {
      observer.disconnect();
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    fetch(`${TIKTOK_OEMBED_BASE_URL}?url=${post.url}`)
      .then((res) => res.json())
      .then((res) => {
        if ((res && res.status_msg) || !res || !res.html) {
          setError('Error getting embedded TikTok post');
          return;
        }
        if (!res || !res.html) {
          setError('Error getting embedded TikTok post');
          return;
        }
        const htmlString = res.html;
        setHTML(htmlString.substr(0, htmlString.indexOf('<script')));
      })
      .catch((err) => {
        setError(err.message);
      });
  }, [post.url]);

  if (error || !_loaded) {
    return <EmbedFallback post={post} />;
  }
  return (
    <>
      <Head>
        <script id="tiktokEmbedder" async src="https://www.tiktok.com/embed.js" />
      </Head>
      <div ref={ref} dangerouslySetInnerHTML={{ __html: html || '' }} />
    </>
  );
}
